import useProductActivationApi from '~/services/apis/product-activation'
import usePurchaseApi from '~/services/apis/purchases-info'
import type { VoidInspectionContext } from '~/components/Dialogs/void-inspection-screen.vue'
import type { McaSdkConfig } from '~/types/mca-javascript-sdk'
import { TimeOut } from '~/services/apis/timeout'
import type { PolicyResponse, RecordSchema } from '@/types'

export default function useStaffActivation(
	planInformation: Ref<PlanInformationContext | null>,
	opts?: { pending?: Ref<boolean>; purchase_id?: string }
) {
	const selectedStaff = ref()
	const searchVal = shallowRef('')
	const { getPendingPolicy, getPendingPolicies } = useProductActivationApi()
	const { queryPolicy, comparePolicy } = usePurchaseApi()
	const { productCategoryType, urlQuery, decryptString, toLower } = useUtils()
	const { policiesTableColumn } = useProducts()
	const { initializeSdk } = useMcaSdk()
	const { deviceType } = useMobile()
	const activateModal = shallowRef(false)
	const fetchingPolicy = shallowRef(false)
	const loadingInspection = shallowRef(false)
	const notSupported = shallowRef(false)
	const fetchCount = ref(0)
	const purchaseId = computed(() => planInformation.value?.purchase_id ?? opts?.purchase_id ?? '')
	const pendingPolicies = ref<PaginationContext<PolicyResponse[]> | null>(null)
	const inspectionProps = reactive<VoidInspectionContext>({
		title: 'Vehicle Inspection',
		key: 'inspections',
		QrCodeLink: '',
		subTitle: 'Kindly scan with your device to proceed'
	})
	const isInspectionNotSupported = shallowRef(false)
	const planName = computed(() => planInformation.value?.product.name?.toLowerCase() || '')
	const isPlanExpired = computed(() => planInformation.value?.is_expired || false)
	const categoryName = computed(() => toLower(planInformation.value?.product.productCategory.name || ''))
	const categoryType = computed(() =>
		productCategoryType(categoryName.value, planInformation.value?.product?.code_name ?? '')
	)
	const isClaimable = computed(() => planInformation.value?.product?.claimable || false)
	const isInspectible = computed(() => planInformation.value?.product?.inspectable || false)

	type InsertColumnOptions<T = TableColumn> = { at: number; rowToAdd: T }
	const getTableRecordColumn = (payload: PlanInformationContext | null, insertOptions?: InsertColumnOptions) => {
		if (!payload?.product?.productCategory?.name) {
			return []
		}
		const category = productCategoryType(payload.product.productCategory.name, payload.product.code_name ?? '')
		let columns: TableColumn[] = []
		const autoColumns = policiesTableColumn.auto
		const comprehensiveColumns = policiesTableColumn.comprehensive
		const gadgetColumns = policiesTableColumn.gadget
		const travelColumns = policiesTableColumn.travel
		const healthColumns = policiesTableColumn.health

		if (category?.isAuto) {
			// @ts-ignore
			columns = [...autoColumns]
		} else if (category?.isComprehensive) {
			columns = [...comprehensiveColumns]
		} else if (category?.isGadget) {
			columns = [...gadgetColumns]
		} else if (category?.isTravel) {
			columns = [...travelColumns]
		} else if (category?.isHealth) {
			columns = [...healthColumns]
		}
		if (insertOptions && typeof insertOptions.at === 'number' && insertOptions.rowToAdd && Array.isArray(columns)) {
			// Avoid duplicates by key
			if (!columns.some(col => col.key === insertOptions.rowToAdd.key)) {
				columns.splice(insertOptions.at, 0, insertOptions.rowToAdd)
			}
		}
		return columns
	}

	const tableRecordColumn = computed(() => getTableRecordColumn(planInformation.value))
	const numberOfStaff = computed(() => {
		return Number(planInformation.value?.total_staff_added || 0)
	})
	const numberOfActivatedStaff = computed(() => Number(planInformation.value?.custodian_activated || 0))
	const numberOfPendingPoliciesCount = computed(() =>
		Number(planInformation.value?.staff_pending_policies_count || 0)
	)
	const pageHeaderStats = computed(() => {
		const addedKey = !categoryType.value.isHealth ? 'Total Policy Holder Added' : 'Total Staff Added '
		const activatedKey = !categoryType.value.isHealth ? 'Policy Holder Activated' : 'Staff Activated'
		return {
			[addedKey]: {
				value: numberOfStaff.value,
				header_class: 'block sm:min-w-[250px]',
				wrapper_class: 'border-success-500'
			},
			[activatedKey]: {
				value: numberOfActivatedStaff.value || 0,
				paragraph_class: 'text-success-500',
				wrapper_class: 'sm:pl-8'
			}
		}
	})
	const currentPage = computed(() => {
		return useRoute().query?.page as 'submitted' | ''
	})
	const planCaption = computed(() => {
		return categoryType.value?.isAuto ? 'vehicle' : categoryType.value?.isGadget ? 'gadget' : 'staff'
	})

	const next = async (arg: typeof currentPage.value, active_plan_id?: string) => {
		if (!active_plan_id) {
			return useNotification().alert({
				status: 400,
				message: 'Something went wrong, Please try again.'
			})
		}
		resolve(async () => {
			if (active_plan_id) {
				refreshNuxtData('plan')
			}
			await navigateTo({
				query: {
					page: arg,
					active_id: active_plan_id || ''
				}
			})
		})
	}
	const openStaffRecord = async (
		policy: {
			purchase_id: string
			items?: PolicyResponse
			customer_id?: string
			preventRequest?: boolean
		},
		fn?: Function
	) => {
		try {
			fetchingPolicy.value = true
			let staffPolicyalreadyExists = false
			const planCategory = String(policy.items?.product?.productCategory?.name ?? '').toLowerCase()
			if (planCategory === ProductCategory.Health) {
				const isNewPolicy = await comparePolicy(
					policy?.purchase_id || purchaseId.value,
					policy?.customer_id || String(policy.items?.customer_id ?? '')
				)
				if (isNewPolicy?.data?.existingPolicy) {
					staffPolicyalreadyExists = true
					notSupported.value = true
				}
			}
			if (!staffPolicyalreadyExists) {
				// This is called directly from the custodian page so no need loading the pending policy  again
				if (policy?.preventRequest) {
					activateModal.value = true
					return null
				}
				const res = await getPendingPolicy<PolicyResponse>({
					param: policy?.items?.id
				})

				if (res) {
					notSupported.value = res?.meta?.is_paused || false
					activateModal.value = !res?.meta?.is_paused
					selectedStaff.value = res
					if (fn) {
						fn()
					}
				}

				return null
			}
		} finally {
			fetchingPolicy.value = false
		}
	}
	const triggerInspection = async () => {
		const { active_id } = urlQuery.value
		if (active_id) {
			try {
				loadingInspection.value = true
				const result = await queryPolicy(String(active_id))
				if (result?.data) {
					const inspectionLink = result.data.meta.inspectionLink
					if (inspectionLink) {
						const link = new URL(inspectionLink)
						const constructed_url = `${location.origin}${link?.search?.replace('?q=', '?inspectionSdk=')}`
						inspectionProps.QrCodeLink = constructed_url
						if (deviceType.value === 'desktop') {
							isInspectionNotSupported.value = true
							return null
						} else {
							const sdk = decryptString<McaSdkConfig>(inspectionLink)
							initializeSdk({
								...sdk,
								onClose: () => refreshNuxtData('plan'),
								callback: () => refreshNuxtData('plan')
							})
						}
					} else if (!inspectionLink && fetchCount.value < 20) {
						await TimeOut(5000)
						fetchCount.value++
						loadingInspection.value = true
						await triggerInspection()
					} else {
						useNotification().alert({
							message: 'Inspection link not available at the moment, please try again later.',
							status: 400
						})
					}
					// else if (categoryType.value?.isGadget) {
					// 	inspectionProps.QrCodeLink = `${location.origin}/pre-inspection/${active_id}`
					// 	inspectionProps.title = 'Gadget Inspection'
					// 	if (deviceType.value === 'desktop') {
					// 		isInspectionNotSupported.value = true
					// 		return null
					// 	}
					// 	navigateTo(inspectionProps.QrCodeLink)
					// }
				}
			} finally {
				loadingInspection.value = false
			}
		} else {
			useNotification().alert({
				status: 400,
				message: 'Plan Id not found'
			})
		}
	}
	const fetchPendingPolicies = async (query: {}, id = purchaseId.value) => {
		if (!id) {
			return null
		}
		const result = await resolve(() => getPendingPolicies({ purchase_id: id, ...query }))
		pendingPolicies.value = result
		return result
	}

	return {
		purchase_id: purchaseId.value,
		isPlanExpired,
		isClaimable,
		searchVal,
		deviceType,
		activateModal,
		fetchingPolicy,
		loadingInspection,
		notSupported,
		inspectionProps,
		isInspectionNotSupported,
		pendingPolicies,
		planName,
		categoryName,
		categoryType,
		tableRecordColumn,
		numberOfActivatedStaff,
		currentPage,
		pageHeaderStats,
		planCaption,
		policiesTableColumn,
		next,
		initializeSdk,
		pending: opts?.pending || false,
		numberOfPendingPoliciesCount,
		selectedStaff,
		urlQuery,
		openStaffRecord,
		triggerInspection,
		fetchPendingPolicies,
		planInformation,
		isInspectible,
		getTableRecordColumn
	}
}
